"use client";

import { cn } from "@/lib/utils";

// ---------------------------------------------------------------- seven-segment
//
// The digits are drawn, not typed. A seven-segment font would have been less
// code, but it would be a webfont to load, license and subset for four digits —
// and it would still be type, so it would fight the rest of the card for size and
// weight instead of sitting in a fixed box.
//
// Each digit is a 1-unit grid the SVG scales; the parent decides how big a clock
// is and nothing here has to change.

/** Which of the seven segments each digit lights, in the usual a..g order. */
const SEGMENTS: Record<string, readonly string[]> = {
  "0": ["a", "b", "c", "d", "e", "f"],
  "1": ["b", "c"],
  "2": ["a", "b", "d", "e", "g"],
  "3": ["a", "b", "c", "d", "g"],
  "4": ["b", "c", "f", "g"],
  "5": ["a", "c", "d", "f", "g"],
  "6": ["a", "c", "d", "e", "f", "g"],
  "7": ["a", "b", "c"],
  "8": ["a", "b", "c", "d", "e", "f", "g"],
  "9": ["a", "b", "c", "d", "f", "g"],
};

// Digit box, in the SVG's own units.
const W = 100;
const H = 180;
// Half the thickness of a bar. Chunky on purpose — this is the look of a cheap
// LCD travel clock, and a thin version just reads as a bad font.
const T = 11;
// Gap between two bars meeting at a corner, so the mitre reads as a seam rather
// than a solid block.
const G = 4;

/**
 * One bar as a hexagon: a rectangle with both ends chamfered to 45°, which is
 * what lets two perpendicular bars meet in a mitre instead of overlapping.
 */
function horizontal(cx: number, cy: number, length: number): string {
  const half = length / 2;

  return [
    `${cx - half} ${cy}`,
    `${cx - half + T} ${cy - T}`,
    `${cx + half - T} ${cy - T}`,
    `${cx + half} ${cy}`,
    `${cx + half - T} ${cy + T}`,
    `${cx - half + T} ${cy + T}`,
  ].join(" L");
}

function vertical(cx: number, cy: number, length: number): string {
  const half = length / 2;

  return [
    `${cx} ${cy - half}`,
    `${cx + T} ${cy - half + T}`,
    `${cx + T} ${cy + half - T}`,
    `${cx} ${cy + half}`,
    `${cx - T} ${cy + half - T}`,
    `${cx - T} ${cy - half + T}`,
  ].join(" L");
}

const BAR_H = W - 2 * T - 2 * G;
const BAR_V = H / 2 - 2 * T - 2 * G;

// a top, b top-right, c bottom-right, d bottom, e bottom-left, f top-left,
// g middle — the standard lettering, so SEGMENTS above reads like every other
// seven-segment table.
const PATHS: Record<string, string> = {
  a: horizontal(W / 2, T + G, BAR_H),
  b: vertical(W - T - G, H / 4 + G / 2, BAR_V),
  c: vertical(W - T - G, (H * 3) / 4 - G / 2, BAR_V),
  d: horizontal(W / 2, H - T - G, BAR_H),
  e: vertical(T + G, (H * 3) / 4 - G / 2, BAR_V),
  f: vertical(T + G, H / 4 + G / 2, BAR_V),
  g: horizontal(W / 2, H / 2, BAR_H),
};

function Digit({ value }: { value: string }) {
  const lit = SEGMENTS[value] ?? [];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-auto" aria-hidden="true">
      {Object.entries(PATHS).map(([name, path]) => (
        <path
          key={name}
          d={`M${path} Z`}
          // Unlit segments are drawn too, very faintly. A real LCD shows its dead
          // segments, and without them a "1" floats unanchored in a box four
          // times its width — the ghosts are what make the digits sit in a row.
          className={
            lit.includes(name) ? "fill-current" : "fill-current opacity-[0.06]"
          }
        />
      ))}
    </svg>
  );
}

function Colon({ className }: { className?: string }) {
  return (
    <svg
      viewBox={`0 0 ${T * 2} ${H}`}
      className={cn("h-full w-auto", className)}
      aria-hidden="true"
    >
      <circle cx={T} cy={H / 3} r={T * 0.85} className="fill-current" />
      <circle cx={T} cy={(H * 2) / 3} r={T * 0.85} className="fill-current" />
    </svg>
  );
}

/**
 * "17:02" in seven-segment digits.
 *
 * Height-driven: the caller sets a height and the digits take whatever width
 * they need, so a clock scales with the card instead of at a fixed pixel size.
 */
export function SevenSegmentTime({
  time,
  className,
}: {
  time: string;
  className?: string;
}) {
  return (
    <div className={cn("flex h-10 items-stretch gap-[3px]", className)}>
      {time.split("").map((character, index) =>
        character === ":" ? (
          <Colon key={index} className="mx-[1px]" />
        ) : (
          <Digit key={index} value={character} />
        ),
      )}
    </div>
  );
}
